/**
 * 句子 → 片段文件名哈希
 * ============================================================
 * 预生成的口型视频按**句子文本**命名，而不是按课程/步骤 id。
 *
 * 为什么按文本而不是按 id：
 *   同一句粤语会出现在多个地方 —— 课内跟读、换词、复习、翻译页收藏。
 *   若按 id 命名，同一句话要生成多份视频（同一段内容存 N 遍）。
 *   按文本命名则**天然去重**：一句一个文件，任何位置都能复用。
 *
 * 为什么用 FNV-1a 而不是 crypto.subtle 的 SHA-1：
 *   crypto.subtle.digest() 是**异步**的，而调用点（播放前的查表）
 *   在渲染路径上，异步哈希会带来没必要的复杂度。
 *   FNV-1a 是同步的、纯函数、几十行就能实现，且 Node 与浏览器结果一致
 *   （本文件同时被 tools/talkinghead/generate-clips.mjs 和前端组件引用，
 *     两边必须算出同一个值 —— 这是唯一硬要求）。
 *
 * 用 64 位（BigInt）而不是 32 位：
 *   32 位在 60 句规模下碰撞概率虽低，但一旦碰撞就是**播错句子**，
 *   这种错误很隐蔽（画面正常，只是嘴型对不上），排查成本高。
 *   64 位把碰撞概率压到可忽略，代价只是一点 BigInt 运算。
 */

const FNV_OFFSET_BASIS = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const MASK_64 = 0xffffffffffffffffn;

/**
 * 计算句子文本的片段标识
 *
 * 归一化：去首尾空白。**不做**繁简转换或标点归一 ——
 * 生成端与播放端拿到的是同一份课程文本，做转换反而可能两边不一致。
 *
 * @param {string} text - 粤语句子原文
 * @returns {string} 16 位十六进制字符串
 */
export function clipHash(text) {
  const normalized = String(text ?? '').trim();
  if (!normalized) return '';

  const bytes = new TextEncoder().encode(normalized);
  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < bytes.length; i++) {
    hash ^= BigInt(bytes[i]);
    hash = (hash * FNV_PRIME) & MASK_64;
  }
  return hash.toString(16).padStart(16, '0');
}

/**
 * 片段在素材目录下的相对路径
 *
 * @param {string} text - 粤语句子
 * @param {string} dir - 片段目录（manifest 里的 clips.dir）
 * @returns {string|null} 无文本时返回 null，调用方据此走降级
 */
export function clipPath(text, dir) {
  const hash = clipHash(text);
  if (!hash || !dir) return null;
  return `${String(dir).replace(/\/+$/, '')}/${hash}.mp4`;
}
